import type { Media } from '../types'

const imageExt = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif']
const videoExt = ['mp4', 'mov', 'webm', 'mkv', 'avi', 'm4v']
const docExt = ['pdf']

const knownVideoHosts = ['youtube.com', 'youtu.be', 'vimeo.com']
const knownDocHosts = ['drive.google.com', 'docs.google.com', 'dropbox.com']

const cleanUrl = (url: string) => url.trim()

const getExtension = (url: string) => {
  try {
    const parsed = new URL(cleanUrl(url))
    const pathname = parsed.pathname.toLowerCase()
    const parts = pathname.split('.')
    return parts.length > 1 ? parts.pop() || '' : ''
  } catch {
    return ''
  }
}

export type MediaKind = Media['type']

export const inferMediaType = (url: string): MediaKind | null => {
  const ext = getExtension(url)
  if (imageExt.includes(ext)) return 'image'
  if (videoExt.includes(ext)) return 'video'
  if (docExt.includes(ext)) return 'document'

  try {
    const host = new URL(cleanUrl(url)).hostname.toLowerCase()
    if (knownVideoHosts.some((h) => host.includes(h))) return 'video'
    if (knownDocHosts.some((h) => host.includes(h))) return 'document'
  } catch {
    return null
  }

  return null
}

export const asEmbedUrl = (url?: string) => {
  if (!url) return undefined
  try {
    const parsed = new URL(cleanUrl(url))
    const host = parsed.hostname.toLowerCase()

    if (host.includes('youtube.com')) {
      const id = parsed.searchParams.get('v')
      return id ? `https://www.youtube.com/embed/${id}` : undefined
    }

    if (host.includes('youtu.be')) {
      const id = parsed.pathname.slice(1)
      return id ? `https://www.youtube.com/embed/${id}` : undefined
    }

    if (host.includes('vimeo.com')) {
      const parts = parsed.pathname.split('/')
      const id = parts.pop()
      return id ? `https://player.vimeo.com/video/${id}` : undefined
    }

    return undefined
  } catch {
    return undefined
  }
}

export const normalizeExternalMedia = (
  url: string,
  overrides?: Partial<Media>
): Media => {
  const type = inferMediaType(url) || 'image'
  const externalUrl = cleanUrl(url)
  return {
    mediaId: crypto.randomUUID(),
    type,
    externalUrl,
    downloadUrl: externalUrl,
    caption: '',
    ...overrides,
  }
}

/**
 * Get YouTube/Vimeo thumbnail URL for preview
 */
export const getVideoThumbnail = (url?: string): string | undefined => {
  if (!url) return undefined
  try {
    const parsed = new URL(cleanUrl(url))
    const host = parsed.host.toLowerCase()

    if (host.includes('youtube.com')) {
      const id = parsed.searchParams.get('v')
      return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : undefined
    }

    if (host.includes('youtu.be')) {
      const id = parsed.pathname.slice(1)
      return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : undefined
    }

    // Vimeo thumbnails require API call, so we'll show a placeholder
    if (host.includes('vimeo.com')) {
      return undefined
    }
  } catch {
    return undefined
  }
  return undefined
}

/**
 * Check if URL is an embeddable video (YouTube/Vimeo)
 */
export const isEmbeddableVideo = (url?: string): boolean => {
  if (!url) return false
  try {
    const parsed = new URL(cleanUrl(url))
    const host = parsed.host.toLowerCase()
    return host.includes('youtube.com') || host.includes('youtu.be') || host.includes('vimeo.com')
  } catch {
    return false
  }
}
